import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormContainer } from 'react-hook-form-mui';
import { UseFormReturn } from 'react-hook-form';
import Multiselect from '../../components/GlobalFilterBar/Multiselect/Multiselect';
import SiteFilter from '../../components/GlobalFilterBar/SiteFilter/SiteFilter';
import ScheduledReportsSearch from './ScheduledReportsSearch';
import { filtersContainerSx, filtersLeftBoxSx } from '../styles';
import {
  createStatusOptions,
  createOccurrenceOptions,
  createScheduledByOptionsFromReports,
} from '../utils/ViewScheduleReportUtil';
import { FilterFormValues, ScheduledReport } from '../types';
import { OrganizationUser } from '../../admin/requests/index';

interface ScheduledReportsFiltersProps {
  formContext: UseFormReturn<FilterFormValues>;
  search: string;
  onSearchChange: (value: string) => void;
  organizationUsers?: OrganizationUser[];
  isLoadingUsers?: boolean;
  reports: ScheduledReport[];
}

function ScheduledReportsFilters({
  formContext,
  search,
  onSearchChange,
  organizationUsers = [],
  isLoadingUsers = false,
  reports,
}: Readonly<ScheduledReportsFiltersProps>) {
  const { t: scheduledReportsT } = useTranslation('translation', {
    keyPrefix: 'admin.scheduledReports',
  });

  // Filter options configuration
  const statusOptions = useMemo(() => createStatusOptions(scheduledReportsT), [scheduledReportsT]);
  const occurrenceOptions = useMemo(() => createOccurrenceOptions(scheduledReportsT), [scheduledReportsT]);
  const scheduledByOptions = useMemo(
    () => createScheduledByOptionsFromReports(reports, organizationUsers),
    [reports, organizationUsers]
  );

  return (
    <FormContainer formContext={formContext}>
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        sx={filtersContainerSx}
        data-testid='scheduled-reports-filters'
      >
        {/* Left side filters */}
        <Box sx={filtersLeftBoxSx}>
          <SiteFilter controllerName='sites' useGlobalSiteDefault={false} />
          <Multiselect
            controllerName='statuses'
            options={statusOptions}
            singular={scheduledReportsT('filters.status')}
            plural={scheduledReportsT('filters.statuses')}
            titleText={scheduledReportsT('filters.status')}
            defaultChecked={false}
            loading={false}
          />
          <Multiselect
            controllerName='occurrences'
            options={occurrenceOptions}
            singular={scheduledReportsT('filters.occurrence')}
            plural={scheduledReportsT('filters.occurrences')}
            titleText={scheduledReportsT('filters.occurrence')}
            defaultChecked={false}
            loading={false}
          />
          <Multiselect
            controllerName='scheduledBy'
            options={scheduledByOptions}
            singular={scheduledReportsT('filters.scheduledBy')}
            plural={scheduledReportsT('filters.scheduledBy')}
            titleText={scheduledReportsT('filters.scheduledBy')}
            defaultChecked={false}
            loading={isLoadingUsers}
          />
        </Box>

        {/* Right side search */}
        <ScheduledReportsSearch search={search} onSearchChange={onSearchChange} />
      </Grid>
    </FormContainer>
  );
}

export default ScheduledReportsFilters;
